import { Plus, Settings } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { InterviewerAssignDialog } from "../../components/term/InterviewerAssignDialog";
import { InterviewerListSection } from "../../components/term/InterviewerListSection";
import { ScheduleConfigDialog } from "../../components/term/ScheduleConfigDialog";
import { ScheduleConfigSection } from "../../components/term/ScheduleConfigSection";
import { TermInterviewListSection } from "../../components/term/TermInterviewListSection";
import { useTermInterviewerAssignment } from "../../components/term/useTermInterviewerAssignment";
import { InterviewType } from "../../graphql/generated/graphql";
import {
  isRemindableRespondent,
  isSendableRespondent,
  type ReportGroup,
  type StakeholderRelation,
  type StakeholderRespondent,
  type StakeholderSetting,
  useCandidateMutations,
  useReportGroupMutations,
  useReportGroups,
  useReportQuestionCandidates,
  useSponsor,
  useStakeholderAnswerMutations,
  useStakeholderAnswers,
  useStakeholderRelationMutations,
  useStakeholderRelations,
  useStakeholderRespondents,
  useStakeholderSettingMutations,
  useStakeholderSettings,
  useTerm,
  useTermScheduleConfig,
  useUnbookedManualReminder,
} from "../../hooks";
import { buildStakeholderRelationsCsv } from "./buildStakeholderRelationsCsv";
import { buildSettingCounts, buildTermSummary } from "./buildTermSummary";
import {
  ReportGroupDialog,
  type ReportGroupForm,
} from "./components/ReportGroupDialog";
import { ReportGroupSection } from "./components/ReportGroupSection";
import { StakeholderAnswerSection } from "./components/StakeholderAnswerSection";
import { StakeholderRelationImportDialog } from "./components/StakeholderRelationImportDialog";
import { StakeholderRelationSection } from "./components/StakeholderRelationSection";
import { StakeholderRespondentSection } from "./components/StakeholderRespondentSection";
import {
  StakeholderSettingDialog,
  type StakeholderSettingForm,
} from "./components/StakeholderSettingDialog";
import { TermSummaryCards } from "./components/TermSummaryCards";
import type { RelationViewAxis } from "./groupStakeholderRelations";
import {
  buildImportConfirmMessage,
  buildImportResultMessage,
} from "./importAnswerMessages";
import { RESPONDENT_TYPE_LABELS } from "./stakeholderSetting";

/**
 * ステークホルダーインタビューのターム詳細。
 *
 * 従業員インタビューのターム詳細（候補者・設問テンプレート前提）とは扱う要素が異なるため、
 * 種別で画面を分けている。設問と要約はスプレッドシートで運用するため、ここでは
 * インタビュー設定（回答者の立場ごとのテンプレートURL）を管理する。
 */
export function StakeholderTermDetail() {
  const navigate = useNavigate();
  const { termId } = useParams<{ termId: string }>();

  const {
    term,
    loading: termLoading,
    error: termError,
  } = useTerm(termId ?? null);
  const { sponsor } = useSponsor(term?.sponsorId ?? null);
  const {
    stakeholderSettings,
    loading: settingsLoading,
    error: settingsError,
    refetch: refetchSettings,
  } = useStakeholderSettings(termId ?? null);
  const {
    createStakeholderSetting,
    updateStakeholderSetting,
    deleteStakeholderSetting,
    error: mutationError,
  } = useStakeholderSettingMutations();

  const {
    stakeholderRelations,
    loading: relationsLoading,
    error: relationsError,
    refetch: refetchRelations,
  } = useStakeholderRelations(termId ?? null);
  const { deleteStakeholderRelation, error: relationMutationError } =
    useStakeholderRelationMutations();

  const {
    stakeholderRespondents,
    loading: respondentsLoading,
    error: respondentsError,
    refetch: refetchRespondents,
  } = useStakeholderRespondents(termId ?? null);

  const {
    stakeholderAnswers,
    loading: answersLoading,
    error: answersLoadError,
    refetch: refetchAnswers,
  } = useStakeholderAnswers(termId ?? null);
  const {
    reportGroups,
    loading: reportGroupsLoading,
    error: reportGroupsError,
    refetch: refetchReportGroups,
  } = useReportGroups(termId ?? null);
  const { reportQuestionCandidates } = useReportQuestionCandidates(
    termId ?? null,
  );
  const {
    createReportGroup,
    updateReportGroup,
    generateReportGroupReports,
    loading: reportGroupWorking,
    error: reportGroupError,
  } = useReportGroupMutations();
  const {
    importStakeholderAnswers,
    createStakeholderAnswerSheets,
    loading: answersWorking,
    error: answersError,
  } = useStakeholderAnswerMutations();
  const {
    sendInvitation,
    bulkSendInvitation,
    loading: sending,
  } = useCandidateMutations();
  const { bulkSendUnbookedManualReminder, loading: reminding } =
    useUnbookedManualReminder();

  const {
    scheduleConfig,
    error: scheduleConfigError,
    refetch: refetchScheduleConfig,
  } = useTermScheduleConfig(termId ?? null);
  const {
    interviewerDataLoadErrors,
    interviewerMutationError,
    interviewerListSectionProps,
    interviewerAssignDialogProps,
  } = useTermInterviewerAssignment(termId ?? null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [scheduleConfigOpen, setScheduleConfigOpen] = useState(false);
  const [editingSetting, setEditingSetting] =
    useState<StakeholderSetting | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [reportGroupDialogOpen, setReportGroupDialogOpen] = useState(false);
  const [editingReportGroup, setEditingReportGroup] =
    useState<ReportGroup | null>(null);
  const [importResultMessage, setImportResultMessage] = useState<string | null>(
    null,
  );

  // 操作の失敗（Mutation）は次の操作まで残り続けるため、先に出すと後から起きた
  // 読み込みの失敗を覆い隠してしまう。表示できるのが1件なので読み込み側を優先する
  const dataLoadError =
    termError ||
    settingsError ||
    relationsError ||
    respondentsError ||
    answersLoadError ||
    scheduleConfigError ||
    reportGroupsError ||
    interviewerDataLoadErrors[0];
  const operationError =
    mutationError ||
    relationMutationError ||
    interviewerMutationError ||
    answersError ||
    reportGroupError;
  const error = dataLoadError || operationError;

  /**
   * 関係性の増減は回答者と回答も動かすため、まとめて取り直す。
   *
   * 関係性だけを取り直すと、下の回答依頼一覧・進み具合・回答が古いまま残り、
   * リロードしないと最新にならない。
   */
  const refetchRelationDependents = () => {
    refetchRelations();
    refetchRespondents();
    refetchAnswers();
  };

  const handleSubmit = async (form: StakeholderSettingForm) => {
    if (!termId) return false;
    const succeeded = editingSetting
      ? await updateStakeholderSetting({
          stakeholderSettingId: editingSetting.stakeholderSettingId,
          ...form,
        })
      : await createStakeholderSetting({ termId, ...form });
    if (succeeded) refetchSettings();
    return succeeded;
  };

  const handleDelete = async (setting: StakeholderSetting) => {
    if (!window.confirm(`「${setting.name}」を削除しますか？`)) return;
    if (await deleteStakeholderSetting(setting.stakeholderSettingId)) {
      refetchSettings();
    }
  };

  const handleDeleteRelation = async (relation: StakeholderRelation) => {
    if (
      !window.confirm(
        `${relation.respondentName} さんが ${relation.recipientName} さんについて語る関係性を削除しますか？\n` +
          "従業員そのものは削除されません。",
      )
    ) {
      return;
    }
    if (await deleteStakeholderRelation(relation.stakeholderRelationId)) {
      setImportResultMessage(null);
      refetchRelationDependents();
    }
  };

  const handleSendRequest = async (respondent: StakeholderRespondent) => {
    if (
      !window.confirm(
        `${respondent.respondentName} さんに回答依頼メールを送信しますか？\n` +
          `受け取る人: ${respondent.recipientNames.join("、")}`,
      )
    ) {
      return;
    }
    const result = await sendInvitation(respondent.candidateId);
    if (result?.success) {
      refetchRespondents();
      alert("回答依頼メールを送信しました");
      return;
    }
    alert(
      result?.errorMessage ||
        "回答依頼メールの送信に失敗しました。時間をおいてから再度お試しください",
    );
  };

  const handleBulkSendRequest = async () => {
    if (!termId) return;
    const sendableCount =
      stakeholderRespondents.filter(isSendableRespondent).length;
    if (
      !window.confirm(
        `回答者${sendableCount}名に回答依頼メールを送信しますか？`,
      )
    ) {
      return;
    }
    const result = await bulkSendInvitation(termId);
    if (!result) {
      alert("一括送信に失敗しました。時間をおいてから再度お試しください");
      return;
    }
    refetchRespondents();
    // 失敗した回答者は理由が本人ごとに違うため、件数だけでなく理由も並べて出す
    const failureReasons = result.results
      .filter((item) => !item.success)
      .map((item) => `・${item.email}: ${item.errorMessage ?? "原因不明"}`);
    alert(
      [
        `送信成功: ${result.successCount}件`,
        `送信失敗: ${result.failureCount}件`,
        ...failureReasons,
        ...(failureReasons.length > 0
          ? [
              "送信できなかった回答者には、原因を直してから各行の「依頼を送信」で再度お試しください",
            ]
          : []),
      ].join("\n"),
    );
  };

  const [relationViewAxis, setRelationViewAxis] =
    useState<RelationViewAxis>("respondent");

  const handleExportRelationsCsv = () => {
    const csv = buildStakeholderRelationsCsv(stakeholderRelations);
    // Excel が UTF-8 と判別できるよう BOM を付ける
    const blob = new Blob([`\ufeff${csv}`], {
      type: "text/csv;charset=utf-8",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `関係性_${term?.title ?? "ターム"}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleAddReportGroup = () => {
    setEditingReportGroup(null);
    setReportGroupDialogOpen(true);
  };

  const handleEditReportGroup = (reportGroup: ReportGroup) => {
    setEditingReportGroup(reportGroup);
    setReportGroupDialogOpen(true);
  };

  const handleSubmitReportGroup = async (form: ReportGroupForm) => {
    const { succeeded, errorMessage } = editingReportGroup
      ? await updateReportGroup({
          reportGroupId: editingReportGroup.reportGroupId,
          name: form.name,
          reportSettingJson: form.reportSettingJson,
        })
      : await createReportGroup({
          termId: termId ?? "",
          name: form.name,
          reportSettingJson: form.reportSettingJson,
        });
    if (succeeded) {
      refetchReportGroups();
      return null;
    }
    return errorMessage ?? "レポート設定の保存に失敗しました";
  };

  const handleGenerateReports = async (reportGroup: ReportGroup) => {
    if (
      !window.confirm(
        `「${reportGroup.name}」のレポートを書き出します。\n取り込み済みの回答から、受け取る人ごとに作られます。すでに作成済みの人は作り直されません。`,
      )
    ) {
      return;
    }
    const { createdCount, errorMessage } = await generateReportGroupReports(
      reportGroup.reportGroupId,
    );
    if (errorMessage !== null) {
      alert(errorMessage);
      return;
    }
    alert(
      createdCount === 0
        ? "新しく作られたレポートはありません。全員ぶん作成済みか、取り込み済みの回答がありません"
        : `レポートを${createdCount}件作成しました`,
    );
  };

  const handleImportAnswers = async (answerIds: string[]) => {
    if (answerIds.length === 0) return;
    if (
      !window.confirm(buildImportConfirmMessage(stakeholderAnswers, answerIds))
    ) {
      return;
    }

    const result = await importStakeholderAnswers(answerIds);
    refetchAnswers();
    if (!result) return;
    alert(buildImportResultMessage(result));
  };

  const handleCreateAnswerSheets = async () => {
    if (!termId) return;
    const result = await createStakeholderAnswerSheets(termId);
    refetchAnswers();
    if (!result) return;
    // 理由をバナー任せにすると、他セクションの読み込みエラーに押し出されて見えなくなる
    alert(
      [
        `シートを作成: ${result.createdCount}件`,
        ...(result.failedCount > 0
          ? [
              `作成失敗: ${result.failedCount}件`,
              `・${result.errorMessage ?? "原因不明"}`,
            ]
          : []),
      ].join("\n"),
    );
  };

  const handleBulkSendReminder = async () => {
    if (!termId) return;
    const remindableCount = stakeholderRespondents.filter(
      isRemindableRespondent,
    ).length;
    if (
      !window.confirm(
        `未予約の回答者${remindableCount}名にリマインドメールを送信しますか？`,
      )
    ) {
      return;
    }
    const outcome = await bulkSendUnbookedManualReminder(termId);
    if (!outcome.ok) {
      alert(outcome.message);
      return;
    }
    refetchRespondents();
    const { result } = outcome;
    if (result.total === 0) {
      alert("リマインドの対象が居ませんでした");
      return;
    }
    // 失敗した回答者は理由が本人ごとに違うため、件数だけでなく理由も並べて出す
    const failureReasons = result.results
      .filter((item) => !item.success)
      .map((item) => `・${item.email}: ${item.errorMessage ?? "原因不明"}`);
    alert(
      [
        `送信成功: ${result.successCount}件`,
        `送信失敗: ${result.failureCount}件`,
        ...failureReasons,
        // 行単位のリマインド導線が無いため、再実行すると成功済みの回答者にも再送される
        ...(failureReasons.length > 0
          ? [
              "原因を直してからもう一度リマインドすると、送信できた回答者にも再度届きます",
            ]
          : []),
      ].join("\n"),
    );
  };

  if (termLoading || settingsLoading) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center font-medium text-[var(--color-text-secondary)]">
          読み込み中...
        </div>
      </div>
    );
  }

  if (!term) {
    return (
      <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="p-10 text-center font-medium text-[#ef4444]">
          タームが見つかりません
        </div>
      </div>
    );
  }

  const settingCounts = buildSettingCounts(
    stakeholderSettings.map((setting) => setting.stakeholderSettingId),
    stakeholderRelations,
    stakeholderAnswers,
  );

  return (
    <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
      <div className="w-full">
        <nav className="mb-4 flex items-center gap-2 text-[13px] text-[var(--color-text-secondary)]">
          <button
            type="button"
            onClick={() => navigate("/employee_interview/sponsors")}
            className="cursor-pointer border-none bg-none p-0 text-[var(--color-primary-500)] text-inherit no-underline hover:underline"
          >
            スポンサー一覧
          </button>
          <span className="text-[#9ca3af]">/</span>
          {sponsor && (
            <>
              <button
                type="button"
                onClick={() =>
                  navigate(`/employee_interview/sponsors/${term.sponsorId}`)
                }
                className="cursor-pointer border-none bg-none p-0 text-[var(--color-primary-500)] text-inherit no-underline hover:underline"
              >
                {sponsor.name}
              </button>
              <span className="text-[#9ca3af]">/</span>
            </>
          )}
          <span className="font-medium text-[var(--color-text-primary)]">
            {term.title}
          </span>
        </nav>

        <div className="mb-8 border-[var(--color-primary-500)] border-l-4 pl-5">
          <div className="mb-2 flex items-center gap-3">
            <h1 className="m-0 font-bold text-[32px] text-[var(--color-primary-500)]">
              {term.title}
            </h1>
            <span className="whitespace-nowrap rounded-full bg-white px-2.5 py-1 font-medium text-[12px] text-[var(--color-text-secondary)]">
              ステークホルダーインタビュー
            </span>
          </div>
          <p className="m-0 font-medium text-[var(--color-text-secondary)] text-sm">
            所要時間 {term.durationMinutes}分
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-white p-4 font-medium text-[#ef4444] text-sm">
            {error}
          </div>
        )}

        {importResultMessage && (
          <div className="mb-4 rounded-md bg-white p-4 font-medium text-[var(--color-text-primary)] text-sm">
            {importResultMessage}
          </div>
        )}

        <TermSummaryCards
          summary={buildTermSummary(stakeholderRespondents, stakeholderAnswers)}
        />

        <div className="flex flex-col gap-6">
          <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="flex items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
              <div>
                <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
                  インタビュー設定
                </h2>
                <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
                  回答者の立場ごとに1つ。ここで指定したシートが対象者の人数ぶん複製されます
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditingSetting(null);
                  setDialogOpen(true);
                }}
                className="flex cursor-pointer items-center gap-1.5 rounded-md border-none bg-[var(--color-primary-500)] px-4 py-2 font-medium text-sm text-white hover:bg-[#045a94]"
              >
                <Plus className="h-4 w-4" />
                設定を追加
              </button>
            </div>
            {stakeholderSettings.length === 0 ? (
              <div className="p-12 text-center">
                <Settings className="mx-auto mb-4 h-12 w-12 text-[#9ca3af]" />
                <p className="text-[var(--color-text-secondary)]">
                  インタビュー設定がありません
                </p>
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[var(--color-bg-subtle-gray)]">
                    <th className="border-[var(--color-border-default)] border-b px-6 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      名前
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-6 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      回答者種別
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-6 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      シート
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-6 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      依頼数
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-6 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      回答数
                    </th>
                    <th className="border-[var(--color-border-default)] border-b px-6 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stakeholderSettings.map((setting) => (
                    <tr
                      key={setting.stakeholderSettingId}
                      className="border-[var(--color-border-default)] border-b"
                    >
                      <td className="px-6 py-4 font-medium text-[var(--color-text-primary)] text-sm">
                        {setting.name}
                      </td>
                      <td className="px-6 py-4 text-[var(--color-text-secondary)] text-sm">
                        {RESPONDENT_TYPE_LABELS[setting.respondentType]}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex gap-3">
                          <a
                            href={setting.scriptTemplateSheetUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[var(--color-primary-500)] hover:underline"
                          >
                            設問
                          </a>
                          <a
                            href={setting.contentsTemplateSheetUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[var(--color-primary-500)] hover:underline"
                          >
                            要約
                          </a>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-[var(--color-text-secondary)] text-sm">
                        {
                          settingCounts.get(setting.stakeholderSettingId)
                            ?.requestedCount
                        }
                      </td>
                      <td className="px-6 py-4 text-[var(--color-text-secondary)] text-sm">
                        {
                          settingCounts.get(setting.stakeholderSettingId)
                            ?.importedAnswerCount
                        }
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingSetting(setting);
                              setDialogOpen(true);
                            }}
                            className="cursor-pointer border-none bg-none p-0 text-[var(--color-primary-500)] hover:underline"
                          >
                            編集
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(setting)}
                            className="cursor-pointer border-none bg-none p-0 text-[#ef4444] hover:underline"
                          >
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <ScheduleConfigSection
            scheduleConfig={scheduleConfig}
            onEdit={() => setScheduleConfigOpen(true)}
          />

          <StakeholderRelationSection
            stakeholderRelations={stakeholderRelations}
            loading={relationsLoading}
            hasSettings={stakeholderSettings.length > 0}
            onImportClick={() => {
              setImportResultMessage(null);
              setImportDialogOpen(true);
            }}
            onDelete={handleDeleteRelation}
            viewAxis={relationViewAxis}
            onViewAxisChange={setRelationViewAxis}
            onExportCsv={handleExportRelationsCsv}
          />

          <StakeholderRespondentSection
            stakeholderRespondents={stakeholderRespondents}
            loading={respondentsLoading}
            sending={sending || reminding}
            onSendRequest={handleSendRequest}
            onBulkSendRequest={handleBulkSendRequest}
            onBulkSendReminder={handleBulkSendReminder}
            onCancelled={refetchRespondents}
          />

          <StakeholderAnswerSection
            stakeholderAnswers={stakeholderAnswers}
            loading={answersLoading}
            loadFailed={answersLoadError !== null}
            working={answersWorking}
            onImport={handleImportAnswers}
            onCreateSheets={handleCreateAnswerSheets}
          />

          <ReportGroupSection
            reportGroups={reportGroups}
            loading={reportGroupsLoading}
            working={reportGroupWorking}
            onAdd={handleAddReportGroup}
            onEdit={handleEditReportGroup}
            onGenerate={handleGenerateReports}
          />

          <InterviewerListSection {...interviewerListSectionProps} />

          <TermInterviewListSection
            termId={termId ?? ""}
            onInterviewCancelled={refetchRespondents}
          />
        </div>

        <ScheduleConfigDialog
          open={scheduleConfigOpen}
          onOpenChange={setScheduleConfigOpen}
          termId={termId ?? null}
          termDurationMinutes={term.durationMinutes}
          interviewType={InterviewType.Stakeholder}
          companyName={sponsor?.name ?? null}
          interviewTitle={term?.title ?? null}
          scheduleConfig={scheduleConfig}
          onSaved={refetchScheduleConfig}
        />

        <InterviewerAssignDialog {...interviewerAssignDialogProps} />

        <StakeholderRelationImportDialog
          open={importDialogOpen}
          termId={termId ?? ""}
          settings={stakeholderSettings}
          onClose={() => setImportDialogOpen(false)}
          onImported={(result) => {
            setImportResultMessage(
              [
                result.createdRelationCount === 0 &&
                result.createdSponsorEmployeeCount === 0
                  ? "入稿データはすべて登録済みでした（氏名の変更は反映されません）"
                  : `関係性${result.createdRelationCount}件（回答者${result.createdRespondentCount}名 / 受け取る人${result.createdRecipientCount}名）を登録し、従業員${result.createdSponsorEmployeeCount}名を新しく作成しました`,
                // 入稿と同時にシートを作るので、作れなかったことも入稿の結果として伝える
                ...(result.failedSheetCount > 0
                  ? [
                      `シートを作成できなかった関係性が${result.failedSheetCount}件あります。回答セクションから作成をやり直してください`,
                    ]
                  : []),
              ].join("\n"),
            );
            refetchRelationDependents();
          }}
        />

        <ReportGroupDialog
          open={reportGroupDialogOpen}
          reportGroup={editingReportGroup}
          questionCandidates={reportQuestionCandidates}
          onClose={() => setReportGroupDialogOpen(false)}
          onSubmit={handleSubmitReportGroup}
        />

        <StakeholderSettingDialog
          open={dialogOpen}
          setting={editingSetting}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  );
}
